import { parseISO } from 'date-fns/parseISO';

import { DIRECTNESS } from '../constants';

import type {
  Strings,
  Context,
  Leg,
  Query,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/DestinationsExplorer';

/**
 * Copied from saddlebag-url due to dependency issue.
 * https://github.skyscannertools.net/web-engineering/saddlebag/blob/master/packages/saddlebag-url/src/param.js
 * @param {Object} object Query parameters in JSON format
 * @param {Boolean} encode [encode=false]
 * @return {string} Query parameters in string format
 */
const param = (object: Query, encode = false) => {
  const parts: string[] = [];
  Object.keys(object)
    .sort()
    .forEach((key) => {
      let value = object[key];
      if (value == null) {
        value = '';
      }
      parts.push(`${key}=${encode ? encodeURIComponent(value) : value}`);
    });
  return parts.join('&');
};

const DAY = 'day';
const MONTH = 'month';

const getDateType = (date: string) => {
  if (date && date.length === 10) {
    return DAY;
  }
  if (date && date.length === 7) {
    return MONTH;
  }
  return null;
};

const formatDate = (date: string) => {
  const type = getDateType(date);
  if (type) {
    return date.replace(/-/g, '').substring(2);
  }
  throw new Error(`invalid date format: ${date}`);
};

const isMultiDestination = (context: Context) =>
  context.tripType === 'multi-destination';
const isReturn = (context: Context) => context.tripType === 'return';

const buildPlacePath = (context: Context) => {
  let path = `/${context.originId.toLowerCase()}`;
  if (context.destinationId) {
    path += `/${context.destinationId.toLowerCase()}`;
  }
  return path;
};

const buildDatePath = (context: Context) => {
  let url = '';
  if (getDateType(context.outboundDate) === DAY) {
    url += `/${formatDate(context.outboundDate)}`;
    if (isReturn(context) && getDateType(context.inboundDate) === DAY) {
      url += `/${formatDate(context.inboundDate)}`;
    }
  }
  return url;
};

const buildUrlPathPrefix = (strings: Strings, context: Context) => {
  if (isMultiDestination(context)) {
    return `/${strings.falcon_de_transport}/d`;
  }
  if (context.destinationId) {
    return `/${strings.falcon_de_transport}/${strings.falcon_de_flights}`;
  }
  return `/${strings.falcon_de_transport}/${strings.falcon_de_flights_from}`;
};

const buildUrlPath = (context: Context) => {
  if (isMultiDestination(context)) {
    let path = '';
    context.legs?.forEach((leg: Leg) => {
      path += `/${leg.originId.toLowerCase()}/${
        leg.date
      }/${leg.destinationId.toLowerCase()}`;
    });
    return path;
  }
  return `${buildPlacePath(context)}${buildDatePath(context)}/`;
};

const buildQueryString = (context: Context) => {
  const query: Query = {
    adultsV2: context.adultsV2 || 1,
    childrenV2: (context.childrenV2 || []).join('|'),
    cabinclass: context.cabinclass || 'economy',
  } as Query;
  if (!isMultiDestination(context)) {
    query.rtn = isReturn(context) ? 1 : 0;
    query.preferdirects = Boolean(context.preferDirects);
    query.outboundaltsenabled = Boolean(context.outboundAlts);
    query.inboundaltsenabled = Boolean(context.inboundAlts);
    if ((context.alternativeorigins || []).length) {
      query.alternativeorigins = context.alternativeorigins?.join(',') || '';
    }
    if ((context.alternativedestinations || []).length) {
      query.alternativedestinations =
        context.alternativedestinations?.join(',') || '';
    }
    if (getDateType(context.outboundDate) === MONTH) {
      query.oym = formatDate(context.outboundDate);
    }
    if (getDateType(context.inboundDate) === MONTH && isReturn(context)) {
      query.iym = formatDate(context.inboundDate);
    }
  }

  if (context.seoAirlineCode) {
    query.seo_airline = context.seoAirlineCode;
  }

  return `?${param(query, true)}`.toLowerCase();
};

const buildUrl = (strings: Strings, context: Context) => {
  if (!context) {
    throw new Error('Context not set');
  }
  if (
    ['multi-destination', 'return', 'one-way'].indexOf(context.tripType) === -1
  ) {
    throw new Error('Invalid tripType');
  }
  return `${buildUrlPathPrefix(strings, context)}${buildUrlPath(
    context,
  )}${buildQueryString(context)}`;
};

const buildBrowseViewLink = (
  originId: string,
  destinationId: string,
  direct: string,
  outboundDateString: string,
  inboundDateString?: string,
) => {
  const path = `/transport/flights/${originId}/${destinationId}`;

  const query: Query = {} as Query;

  const oDate = parseISO(outboundDateString);
  query.oym =
    oDate.getFullYear().toString().slice(-2) +
    `0${oDate.getMonth() + 1}`.slice(-2);
  query.selectedoday = `0${oDate.getDate()}`.slice(-2);

  if (inboundDateString) {
    const iDate = parseISO(inboundDateString);
    query.iym =
      iDate.getFullYear().toString().slice(-2) +
      `0${iDate.getMonth() + 1}`.slice(-2);
    query.selectediday = `0${iDate.getDate()}`.slice(-2);

    query.rtn = 1;
  } else {
    query.rtn = 0;
  }

  query.preferdirects = direct === DIRECTNESS.DIRECT;

  return path + `?${param(query, true)}`.toLowerCase();
};

export {
  buildUrlPath,
  buildPlacePath,
  buildUrlPathPrefix,
  buildQueryString,
  buildBrowseViewLink,
};
export default buildUrl;
